import { useEffect, useState } from 'react';
import { ProfileCard, ChangePasswordCard } from '@podus/ui';
import { podusApi, type PodusUserProfile } from '@/lib/podusApi';

type MobileTab = 'account' | 'security';

export default function UserSettings() {
    const [profile, setProfile] = useState<PodusUserProfile | null>(null);
    const [mobileTab, setMobileTab] = useState<MobileTab>('account');

    const fetchProfile = async () => {
        try {
            const data = await podusApi.get<PodusUserProfile>('/users/profile');
            setProfile(data);
        } catch (err) {
            console.error('Failed to load profile:', err);
        }
    };

    useEffect(() => {
        fetchProfile();
    }, []);

    const cardProfile = profile
        ? {
              name: `${profile.firstName ?? ''} ${profile.lastName ?? ''}`.trim() || '—',
              email: profile.email,
              phone: profile.phoneNumber,
              role: 'Podus Multidoor User',
          }
        : null;

    const handleSave = async (updates: { name: string; phone: string }) => {
        const [firstName, ...rest] = updates.name.trim().split(/\s+/);
        const lastName = rest.join(' ');
        await podusApi.post('/users/edit-profile', {
            firstName,
            lastName,
            phoneNumber: updates.phone,
        });
        await fetchProfile();
    };

    const handleChangePassword = async (data: {
        oldPassword: string;
        newPassword: string;
        confirmPassword: string;
    }) => {
        await podusApi.post('/users/change-password', data);
    };

    return (
        <div>
            {/* Header */}
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-secondary sm:text-3xl">Settings</h1>
            </div>

            {/* Mobile-only tabs */}
            <div className="mt-6 flex gap-3 sm:hidden">
                <button
                    type="button"
                    onClick={() => setMobileTab('account')}
                    className={`rounded-lg px-5 py-2.5 text-[13px] font-semibold transition-colors ${
                        mobileTab === 'account'
                            ? 'bg-secondary text-white'
                            : 'border border-[#eee] text-secondary hover:bg-[#f5f5f5]'
                    }`}
                >
                    Account Settings
                </button>
                <button
                    type="button"
                    onClick={() => setMobileTab('security')}
                    className={`rounded-lg px-5 py-2.5 text-[13px] font-semibold transition-colors ${
                        mobileTab === 'security'
                            ? 'bg-secondary text-white'
                            : 'border border-[#eee] text-secondary hover:bg-[#f5f5f5]'
                    }`}
                >
                    Security
                </button>
            </div>

            {/* Mobile section heading */}
            <div className="mt-6 sm:hidden">
                <h2 className="text-lg font-bold text-secondary">
                    {mobileTab === 'account' ? 'Personal Details' : 'Password'}
                </h2>
                <p className="mt-1 text-[13px] text-[#888]">
                    {mobileTab === 'account'
                        ? 'Manage your name, contact, and other account information.'
                        : 'Keep your account safe by choosing a strong password.'}
                </p>
            </div>

            {/* Content */}
            <div className="mt-5 grid gap-5 sm:mt-6 sm:grid-cols-2">
                {/* Profile card — always on desktop, only on account tab on mobile */}
                <div className={mobileTab === 'account' ? 'block' : 'hidden sm:block'}>
                    <ProfileCard profile={cardProfile} accountLabel="Podus Multidoor User" onSave={handleSave} />
                </div>

                {/* Change password — always on desktop, only on security tab on mobile */}
                <div className={mobileTab === 'security' ? 'block' : 'hidden sm:block'}>
                    <ChangePasswordCard onSubmit={handleChangePassword} />
                </div>
            </div>
        </div>
    );
}
